import posthog from 'posthog-js';
import { DateString } from '../../shared/types';
import { AppInfo } from '../environment';

export type SmartFixSolution = 'agent-llm' | 'rag' | 'chat-with-docs' | 'Voice to Voice';

type SmartFixTrackingEventProperties = {
  productType: string;
  isHeatingPlan: boolean;
  solution: SmartFixSolution | 'unknown';
  brand: string;
};

type SmartFixEmbeddedTrackingEvent = Omit<SmartFixTrackingEventProperties, 'solution' | 'isHeatingPlan' | 'brand'>;

type SmartFixVoice2VoiceTrackingEvent = Omit<SmartFixTrackingEventProperties, 'solution' | 'isHeatingPlan'>;

type SmartFixVoice2VoiceStopTrackingEvent = SmartFixVoice2VoiceTrackingEvent & {
  voice2voiceSessionDuration: string;
};

// Use this to define event keys and the data you should be sending with them!
type TrackingEvents = {
  date_booked_tracking: {
    productType: string;
    earliestDate: DateString;
    selectedDate: DateString;
    manufacturer: string;
    bff: boolean;
    bookingOEM: string;
    latestPossibleDate: DateString;
    numberOfDaysFromEarliestDate: number;
    numberOfDaysFromToday: number;
    numberOfDaysFromTodayToEarliestDate: number;
  };

  // Smart fix PostHog events
  landed_on_smart_fix_page: SmartFixTrackingEventProperties;
  clicked_try_CTA: SmartFixTrackingEventProperties;
  on_try_smart_fix: SmartFixTrackingEventProperties;
  continue_to_booking_form_from_smart_fix: SmartFixTrackingEventProperties;
  smart_fix_on_request_feedback: SmartFixTrackingEventProperties;
  smart_fix_start_triage_questions: SmartFixTrackingEventProperties;
  smart_fix_initialise_solution: SmartFixTrackingEventProperties;
  smart_fix_end_chat: SmartFixTrackingEventProperties;
  smart_fix_start_safety_check: SmartFixTrackingEventProperties;
  smart_fix_safety_check_answer: SmartFixTrackingEventProperties & { hasBurningSmell: boolean };
  smart_fix_fire_check_answer: SmartFixTrackingEventProperties & { isOnFire: boolean };
  smart_fix_submit_feedback: SmartFixTrackingEventProperties & {
    rating: string | null;
    resolved: string | null;
    feedbackText: string | null;
    surveyFeedbackResponses?: Record<string, string>;
  };
  smart_fix_skip_to_book_engineer: SmartFixTrackingEventProperties;
  smart_fix_continue_conversation_rag: SmartFixTrackingEventProperties;
  smart_fix_continue_conversation_agent_llm: SmartFixTrackingEventProperties;
  smart_fix_start_voice_capture: SmartFixTrackingEventProperties;
  smart_fix_pause_voice_capture: SmartFixTrackingEventProperties;
  smart_fix_close_interaction_mode_dialog: SmartFixTrackingEventProperties;

  // Smart fix voice to voice events
  smart_fix_viewed_popup_voice2voice: SmartFixVoice2VoiceTrackingEvent;
  smart_fix_start_voice2voice: SmartFixVoice2VoiceTrackingEvent;
  smart_fix_stop_voice2voice: SmartFixVoice2VoiceStopTrackingEvent;
  smart_fix_error_starting_voice2voice: SmartFixVoice2VoiceTrackingEvent;

  // Smart fix video guide (embedded fix) events
  smart_fix_video_guide_viewed: SmartFixEmbeddedTrackingEvent;
  smart_fix_video_guide_opened: SmartFixEmbeddedTrackingEvent;
  smart_fix_video_guide_resolved: SmartFixEmbeddedTrackingEvent;
  smart_fix_video_guide_still_needs_repair: SmartFixEmbeddedTrackingEvent;
  smart_fix_image_attach_viewed: SmartFixEmbeddedTrackingEvent;
  smart_fix_image_upload_attempt: SmartFixTrackingEventProperties;
  smart_fix_image_upload_success: SmartFixTrackingEventProperties;
  smart_fix_image_upload_failure: SmartFixTrackingEventProperties & { failureReason: string };

  booking_confirmed: {};
};

type TrackingEventKeys = keyof TrackingEvents;
export namespace Tracking {
  export function initialise(): void {
    // needed to track user from claims form into this part of the app
    const hashParams = new URLSearchParams(window.location.hash.substring(1));
    const distinctID = hashParams.get('distinctId');
    const sessionID = hashParams.get('sessionId');

    posthog.init(AppInfo.Environment.posthog.key, {
      api_host: AppInfo.Environment.posthog.host,
      person_profiles: 'identified_only',
      defaults: '2025-05-24',
      bootstrap: {
        distinctID: distinctID ?? undefined,
        sessionID: sessionID ?? undefined,
      },
    });

    window['dataLayer'] = window['dataLayer'] || [];
    window['dataLayer'].push({
      'gtm.start': new Date().getTime(),
      event: 'gtm.js',
    });
  }

  export function capture<TKey extends TrackingEventKeys>(key: TKey, properties: TrackingEvents[TKey]): void {
    if (AppInfo.Environment.environment !== 'production') {
      console.log(`Event tracking fired for key: ${key}`);
      console.table(properties);
    }

    posthog.capture(key, properties);
  }

  // This is used to auto track page views and is triggered in page ./src/app/shared/routing/routing-utils.ts
  export function capturePageView(): void {
    posthog.capture('$pageview');
  }
}
